import EventEmitter from 'event-lite';
import type { VNodeProps } from 'vue';
import type AppointmentArchive from './components/modal/appointment/Archive.vue';
import type AppointmentEdit from './components/modal/appointment/Edit.vue';
import type AppointmentLink from './components/modal/dialog/AppointmentLink.vue';
import type AppointmentShow from './components/modal/appointment/Show.vue';
import type AssignmentArchive from './components/modal/assignment/Archive.vue';
import type AssignmentEdit from './components/modal/assignment/Edit.vue';
import type AssignmentShow from './components/modal/assignment/Show.vue';
import type ChanceArchive from './components/modal/chance/Archive.vue';
import type ChanceConversion from './components/modal/chance/Conversion.vue';
import type ChanceEdit from './components/modal/chance/Edit.vue';
import type ChanceShow from './components/modal/chance/Show.vue';
import type ContactArchive from './components/modal/contact/Archive.vue';
import type ContactEdit from './components/modal/contact/Edit.vue';
import type ContactShow from './components/modal/contact/Show.vue';
import type ContractArchive from './components/modal/contract/Archive.vue';
import type ContractEdit from './components/modal/contract/Edit.vue';
import type ContractShow from './components/modal/contract/Show.vue';
import type CustomerEdit from './components/modal/customer/Edit.vue';
import type CustomerLink from './components/modal/customer/Link.vue';
import type CustomerLinkEdit from './components/modal/customer/LinkEdit.vue';
import type CustomerMoreInfo from './components/modal/customer/MoreInfo.vue';
import type CustomerNote from './components/modal/customer/Note.vue';
import type DialogAssociated from './components/modal/dialog/Associated.vue';
import type DialogCustom from './components/modal/dialog/Custom.vue';
import type DialogFilterChance from './components/modal/dialog/filter/Chance.vue';
import type DialogFilterEdit from './components/modal/dialog/filter/Edit.vue';
import type DialogRadioButton from './components/modal/dialog/RadioButton.vue';
import type EmailArchive from './components/modal/email/Archive.vue';
import type EmailLink from './components/modal/email/Link.vue';
import type EmailRead from './components/modal/email/Read.vue';
import type EmailShow from './components/modal/email/Show.vue';
import type FieldEdit from './components/modal/field/Edit.vue';
import type FinancialShow from './components/modal/financial/Show.vue';
import type ImportAppointments from './components/modal/import/Appointments.vue';
import type ImportAssignments from './components/modal/import/Assignments.vue';
import type ImportChances from './components/modal/import/Chances.vue';
import type ImportContacts from './components/modal/import/Contacts.vue';
import type ImportContracts from './components/modal/import/Contracts.vue';
import type ImportCustomers from './components/modal/import/Customers.vue';
import type ImportNotes from './components/modal/import/Notes.vue';
import type ImportTasks from './components/modal/import/Tasks.vue';
import type IntegrationGoogleCalendar from './components/modal/integration/GoogleCalendar.vue';
import type IntegrationOutlookCalendar from './components/modal/integration/OutlookCalendar.vue';
import type GoogleSelector from './components/modal/dialog/GoogleSelector.vue';
import type KVKSelector from './components/modal/dialog/KVKSelector.vue';
import type PostalCode from './components/modal/dialog/PostalCode.vue';
import type LegacyTaskShow from './components/modal/task/Show.vue';
import type MediaEdit from './components/modal/media/Edit.vue';
import type MiscChanceFollowup from './components/modal/misc/ChanceFollowup.vue';
import type MiscFollowUp from './components/modal/misc/FollowUp.vue';
import type MiscMedia from './components/modal/misc/Media.vue';
import type Move from './components/modal/misc/Move.vue';
import type MoveAppointment from './components/modal/misc/MoveAppointment.vue';
import type MoveContract from './components/modal/misc/MoveContract.vue';
import type NoteArchive from './components/modal/note/Archive.vue';
import type NoteEdit from './components/modal/note/Edit.vue';
import type NoteShow from './components/modal/note/Show.vue';
import type NotificationArchive from './components/modal/notification/Archive.vue';
import type ClientEdit from './components/modal/client/Edit.vue';
import type ClientShow from './components/modal/client/Show.vue';
import type PasswordEdit from './components/modal/password/Edit.vue';
import type RoleEdit from './components/modal/role/Edit.vue';
import type RoleSave from './components/modal/dialog/RoleSave.vue';
import type SubtaskEdit from './components/modal/subtask/Edit.vue';
import type SubscriptionEdit from './components/modal/subscription/Edit.vue';
import type TaskArchive from './components/modal/task/Archive.vue';
import type TaskEdit from './components/modal/task/Edit.vue';
import type TemplateEdit from './components/modal/template/Edit.vue';
import type TodoArchive from './components/modal/todo/Archive.vue';
import type TodoEdit from './components/modal/todo/Edit.vue';
import type UserEdit from './components/modal/user/Edit.vue';
import type WebhookEdit from './components/modal/webhook/Edit.vue';
import type MoveCustomer from './components/modal/misc/MoveCustomer.vue';
import type EmailEdit from './components/modal/misc/EmailEdit.vue';

type ComponentProps<C extends Component> = C extends new (...args: any) => any
    ? Omit<InstanceType<C>['$props'], keyof VNodeProps>
    : never;

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
type MaybeOptionalComponentProps<C extends Component> = {} extends ComponentProps<C>
    ? [props?: ComponentProps<C>]
    : [ComponentProps<C>];

export type ReplaceType<K extends keyof Events = keyof Omit<Events, 'replace'>> = [key: K, data: Events[K]];
export type OnCreated<T> = (model: T) => any;

type E = Record<`refresh.${string}` | `updated.${string}` | `deleted.${string}` | `created.${string}`, [model: any]>;

/**
 * Should be either empty array or array with one object
 */
export interface Events extends E {
    '*': [{ name: keyof Events; data?: Events[keyof Events][0] }];
    'close': [event?: keyof Events];
    'close.all': [];
    'modal.appointment.archive': MaybeOptionalComponentProps<typeof AppointmentArchive>;
    'modal.appointment.edit': MaybeOptionalComponentProps<typeof AppointmentEdit>;
    'modal.appointment.show': MaybeOptionalComponentProps<typeof AppointmentShow>;
    'modal.assignment.archive': MaybeOptionalComponentProps<typeof AssignmentArchive>;
    'modal.assignment.edit': MaybeOptionalComponentProps<typeof AssignmentEdit>;
    'modal.assignment.show': MaybeOptionalComponentProps<typeof AssignmentShow>;
    'modal.chance.archive': MaybeOptionalComponentProps<typeof ChanceArchive>;
    'modal.chance.conversion': MaybeOptionalComponentProps<typeof ChanceConversion>;
    'modal.chance.edit': MaybeOptionalComponentProps<typeof ChanceEdit>;
    'modal.chance.show': MaybeOptionalComponentProps<typeof ChanceShow>;
    'modal.contact.archive': MaybeOptionalComponentProps<typeof ContactArchive>;
    'modal.contact.edit': MaybeOptionalComponentProps<typeof ContactEdit>;
    'modal.contact.show': MaybeOptionalComponentProps<typeof ContactShow>;
    'modal.contract.archive': MaybeOptionalComponentProps<typeof ContractArchive>;
    'modal.contract.edit': MaybeOptionalComponentProps<typeof ContractEdit>;
    'modal.contract.show': MaybeOptionalComponentProps<typeof ContractShow>;
    'modal.customer.edit': MaybeOptionalComponentProps<typeof CustomerEdit>;
    'modal.customer.link_edit': MaybeOptionalComponentProps<typeof CustomerLinkEdit>;
    'modal.customer.link': MaybeOptionalComponentProps<typeof CustomerLink>;
    'modal.customer.more_info': MaybeOptionalComponentProps<typeof CustomerMoreInfo>;
    'modal.customer.note': MaybeOptionalComponentProps<typeof CustomerNote>;
    'modal.dialog.appointment_link': MaybeOptionalComponentProps<typeof AppointmentLink>;
    'modal.dialog.associated': MaybeOptionalComponentProps<typeof DialogAssociated>;
    'modal.dialog.custom': MaybeOptionalComponentProps<typeof DialogCustom>;
    'modal.dialog.filter.chance': MaybeOptionalComponentProps<typeof DialogFilterChance>;
    'modal.dialog.filter.edit': MaybeOptionalComponentProps<typeof DialogFilterEdit>;
    'modal.dialog.google_selector': MaybeOptionalComponentProps<typeof GoogleSelector>;
    'modal.dialog.kvk_selector': MaybeOptionalComponentProps<typeof KVKSelector>;
    'modal.dialog.postal_code': MaybeOptionalComponentProps<typeof PostalCode>;
    'modal.dialog.radio_button': MaybeOptionalComponentProps<typeof DialogRadioButton>;
    'modal.dialog.role_save': MaybeOptionalComponentProps<typeof RoleSave>;
    'modal.email.archive': MaybeOptionalComponentProps<typeof EmailArchive>;
    'modal.email.link': MaybeOptionalComponentProps<typeof EmailLink>;
    'modal.email.read': MaybeOptionalComponentProps<typeof EmailRead>;
    'modal.email.show': MaybeOptionalComponentProps<typeof EmailShow>;
    'modal.field.edit': MaybeOptionalComponentProps<typeof FieldEdit>;
    'modal.financial.show': MaybeOptionalComponentProps<typeof FinancialShow>;
    'modal.import.appointments': MaybeOptionalComponentProps<typeof ImportAppointments>;
    'modal.import.assignments': MaybeOptionalComponentProps<typeof ImportAssignments>;
    'modal.import.chances': MaybeOptionalComponentProps<typeof ImportChances>;
    'modal.import.contacts': MaybeOptionalComponentProps<typeof ImportContacts>;
    'modal.import.contracts': MaybeOptionalComponentProps<typeof ImportContracts>;
    'modal.import.customers': MaybeOptionalComponentProps<typeof ImportCustomers>;
    'modal.import.notes': MaybeOptionalComponentProps<typeof ImportNotes>;
    'modal.import.tasks': MaybeOptionalComponentProps<typeof ImportTasks>;
    'modal.integration.google_calendar': MaybeOptionalComponentProps<typeof IntegrationGoogleCalendar>;
    'modal.integration.outlook_calendar': MaybeOptionalComponentProps<typeof IntegrationOutlookCalendar>;
    'modal.media.edit': MaybeOptionalComponentProps<typeof MediaEdit>;
    'modal.misc.chance_followup': MaybeOptionalComponentProps<typeof MiscChanceFollowup>;
    'modal.misc.follow_up': MaybeOptionalComponentProps<typeof MiscFollowUp>;
    'modal.misc.media': MaybeOptionalComponentProps<typeof MiscMedia>;
    'modal.misc.move_appointment': MaybeOptionalComponentProps<typeof MoveAppointment>;
    'modal.misc.move_contract': MaybeOptionalComponentProps<typeof MoveContract>;
    'modal.misc.move_customer': MaybeOptionalComponentProps<typeof MoveCustomer>;
    'modal.misc.email_edit': MaybeOptionalComponentProps<typeof EmailEdit>;
    'modal.misc.move': MaybeOptionalComponentProps<typeof Move>;
    'modal.note.archive': MaybeOptionalComponentProps<typeof NoteArchive>;
    'modal.note.edit': MaybeOptionalComponentProps<typeof NoteEdit>;
    'modal.note.show': MaybeOptionalComponentProps<typeof NoteShow>;
    'modal.notification.archive': MaybeOptionalComponentProps<typeof NotificationArchive>;
    'modal.client.edit': MaybeOptionalComponentProps<typeof ClientEdit>;
    'modal.client.show': MaybeOptionalComponentProps<typeof ClientShow>;
    'modal.password.edit': MaybeOptionalComponentProps<typeof PasswordEdit>;
    'modal.role.edit': MaybeOptionalComponentProps<typeof RoleEdit>;
    'modal.subscription.edit': MaybeOptionalComponentProps<typeof SubscriptionEdit>;
    'modal.subtask.edit': MaybeOptionalComponentProps<typeof SubtaskEdit>;
    'modal.task.archive': MaybeOptionalComponentProps<typeof TaskArchive>;
    'modal.task.edit': MaybeOptionalComponentProps<typeof TaskEdit>;
    'modal.task.show': MaybeOptionalComponentProps<typeof LegacyTaskShow>;
    'modal.template.edit': MaybeOptionalComponentProps<typeof TemplateEdit>;
    'modal.todo.archive': MaybeOptionalComponentProps<typeof TodoArchive>;
    'modal.todo.edit': MaybeOptionalComponentProps<typeof TodoEdit>;
    'modal.user.edit': MaybeOptionalComponentProps<typeof UserEdit>;
    'modal.webhook.edit': MaybeOptionalComponentProps<typeof WebhookEdit>;
}

const eventBus = new EventEmitter<Events>();

// All emits also emit a '*' event for WorkFlow listeners.
const orgEmit = eventBus.emit.bind(eventBus);
eventBus.emit = function <K extends keyof Events>(event: K, ...data: Events[K]) {
    if (event === '*') {
        return orgEmit.call(this, event as any, ...data as []);
    }

    const [first, ...rest] = data as any[];
    orgEmit.call(this, '*', { name: event, data: first } satisfies Events['*'][0]);
    return (orgEmit as any).call(this, event, first, ...rest);
};

if (import.meta.env.DEV) {
    // @ts-expect-error adding eventBus to window
    window.eventBus = eventBus;
}

export default eventBus;
